import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, X } from 'lucide-react';
import { api } from '../../services/api.js';
import type { Distributor, Product } from '../../types';
import type { OpenOwnOrder, PosOrderGroup } from '../../utils/pickListGrouping';
import { extractApiError } from '../../utils/pdfDownload';
import {
  defaultOrderDistributorId,
  sortDistributorsForOrderCapture,
} from '../../utils/orderDistributorSort';
import {
  EditOrderLineFields,
  type OrderLineDraft,
  type PromotionType,
} from './EditOrderLineFields';

const controlClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500';

function fromOrder(order: OpenOwnOrder): OrderLineDraft {
  return {
    key: order.id,
    id: order.id,
    productId: order.product_id,
    variantId: order.variant_id || '',
    quantity: String(order.quantity),
    promotionType: (order.promotion_type as PromotionType) || 'none',
    priceOverride:
      order.unit_price_override != null ? String(order.unit_price_override) : '',
    notes: order.notes || '',
    picked: order.picked,
  };
}

function emptyLine(): OrderLineDraft {
  return {
    key: `new-${Date.now()}`,
    productId: '',
    variantId: '',
    quantity: '1',
    promotionType: 'none',
    priceOverride: '',
    notes: '',
  };
}

function parseLine(line: OrderLineDraft): {
  quantity: number;
  override: number | null;
} | null {
  const quantity = Number(line.quantity);
  if (!line.productId || !Number.isInteger(quantity) || quantity < 1) return null;
  const override =
    line.priceOverride.trim() === '' ? null : Number(line.priceOverride);
  if (override !== null && (!Number.isFinite(override) || override < 0)) return null;
  return { quantity, override };
}

interface EditPosOrderSheetProps {
  group: PosOrderGroup;
  onClose: () => void;
  onSaved: () => void;
}

export const EditPosOrderSheet: React.FC<EditPosOrderSheetProps> = ({
  group,
  onClose,
  onSaved,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [distributorId, setDistributorId] = useState(
    group.orders[0]?.distributor_id || ''
  );
  const [lines, setLines] = useState<OrderLineDraft[]>(
    group.orders.map(fromOrder)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api
      .get('/products?is_active=true')
      .then((res) => setProducts(res.data))
      .catch(() => toast.error('Failed to load products'));
    void api
      .get('/distributors')
      .then((res) => {
        const sorted = sortDistributorsForOrderCapture(res.data as Distributor[]);
        setDistributors(sorted);
        if (!group.orders[0]?.distributor_id) {
          setDistributorId(defaultOrderDistributorId(sorted));
        }
      })
      .catch(() => {});
  }, [group.orders]);

  const setLine = (key: string, next: OrderLineDraft) => {
    setLines((prev) => prev.map((row) => (row.key === key ? next : row)));
  };

  const handleSave = async () => {
    if (!group.posId) {
      toast.warning('This group has no PoS and cannot be edited here');
      return;
    }
    const parsed = lines.map((line) => ({ line, values: parseLine(line) }));
    if (parsed.some((row) => !row.values)) {
      toast.warning('Each line needs a product and a quantity of at least 1');
      return;
    }
    setSaving(true);
    try {
      await saveGroup({
        posId: group.posId,
        original: group.orders,
        lines: parsed as Array<{ line: OrderLineDraft; values: NonNullable<ReturnType<typeof parseLine>> }>,
        distributorId,
      });
      toast.success('Order updated');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(extractApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-gray-900">Edit order</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            {group.posName}
            {group.posCity ? ` · ${group.posCity}` : ''}
          </p>
          <label className="block text-sm font-medium text-gray-700">
            Distributor
            <select
              value={distributorId}
              onChange={(e) => setDistributorId(e.target.value)}
              className={`${controlClass} mt-1`}
            >
              {distributors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          {lines.map((line, index) => (
            <div key={line.key} className="rounded-xl border border-gray-200 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wider text-gray-400">
                  Line {index + 1}
                  {line.picked ? ' · Picked' : ''}
                </p>
                {line.picked ? (
                  <span className="text-[10px] font-bold text-gray-400">
                    Remove on the delivery note
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      setLines((prev) => prev.filter((row) => row.key !== line.key))
                    }
                    className="text-red-600 p-1"
                    aria-label="Remove line"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <EditOrderLineFields
                products={products}
                line={line}
                controlClass={controlClass}
                onChange={(next) => setLine(line.key, next)}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => setLines((prev) => [...prev, emptyLine()])}
            className="w-full min-h-[44px] rounded-xl border-2 border-dashed border-gray-300 font-bold text-sm flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Add line
          </button>
          <button
            type="button"
            disabled={saving || lines.length === 0}
            onClick={() => void handleSave()}
            className="w-full min-h-[44px] rounded-xl bg-brand-600 text-white font-bold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

type ParsedSheetLine = {
  line: OrderLineDraft;
  values: { quantity: number; override: number | null };
};

function patchBody(row: ParsedSheetLine) {
  return {
    product_id: row.line.productId,
    variant_id: row.line.variantId || null,
    quantity: row.values.quantity,
    promotion_type: row.line.promotionType,
    unit_price_override: row.values.override,
    notes: row.line.notes.trim() || null,
  };
}

function createBody(
  posId: string,
  distributorId: string,
  row: ParsedSheetLine
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    pos_id: posId,
    product_id: row.line.productId,
    quantity: row.values.quantity,
    distributor_id: distributorId,
    promotion_type: row.line.promotionType,
    source: 'manual',
  };
  if (row.line.variantId) body.variant_id = row.line.variantId;
  if (row.values.override != null) body.unit_price_override = row.values.override;
  if (row.line.notes.trim()) body.notes = row.line.notes.trim();
  return body;
}

async function saveGroup(input: {
  posId: string;
  original: OpenOwnOrder[];
  distributorId: string;
  lines: ParsedSheetLine[];
}): Promise<void> {
  const originalIds = new Set(input.original.map((o) => o.id));
  const keptIds = new Set(
    input.lines.map((row) => row.line.id).filter((id): id is string => Boolean(id))
  );
  const removed = input.original
    .filter((o) => !keptIds.has(o.id) && !o.picked)
    .map((o) => o.id);
  if (removed.length > 0) {
    await api.post('/orders/bulk-delete', { ids: removed });
  }
  const firstDist = input.original[0]?.distributor_id;
  if (input.distributorId && input.distributorId !== firstDist && keptIds.size > 0) {
    await api.post('/orders/bulk-distributor', {
      ids: [...keptIds],
      distributor_id: input.distributorId,
    });
  }
  for (const row of input.lines) {
    if (row.line.id && originalIds.has(row.line.id)) {
      await api.patch(`/orders/${row.line.id}`, patchBody(row));
      continue;
    }
    await api.post('/orders', createBody(input.posId, input.distributorId, row));
  }
}
